import { AccessControlRequestTemplate } from '@agilelab/plugin-wb-builder-common';
import { ComponentInstanceEntity } from '@agilelab/plugin-wb-marketplace-common';

export type TemplateGroup = {
  template: AccessControlRequestTemplate;
  technologyName: string | undefined;
  outputPorts: ComponentInstanceEntity[];
};

function toIdArray(v: string | string[] | undefined): string[] {
  if (v === undefined) return [];
  return Array.isArray(v) ? v : [v];
}

/**
 * Resolves a template for a single output port using the priority hierarchy:
 * 1. Template matching the port's own `useCaseTemplateId`
 * 2. Template matching the product's `useCaseTemplateId`
 * 3. Default template (no `useCaseTemplateId`)
 */
function resolveTemplateForPort(
  port: ComponentInstanceEntity,
  templates: AccessControlRequestTemplate[],
  productUseCaseTemplateId: string | undefined,
): AccessControlRequestTemplate | undefined {
  const consumableId = port.descriptor?.useCaseTemplateId as string | undefined;

  for (const id of [consumableId, productUseCaseTemplateId].filter(
    Boolean,
  ) as string[]) {
    const match = templates.find(t =>
      toIdArray(t.spec.useCaseTemplateId).includes(id),
    );
    if (match) return match;
  }

  return templates.find(t => toIdArray(t.spec.useCaseTemplateId).length === 0);
}

/**
 * Groups output ports by their technology name.
 * Ports that share the same technology are placed in the same group.
 * Each group uses the template resolved for the first port in that technology.
 */
export function resolveTemplateGroups(
  outputPorts: ComponentInstanceEntity[],
  templates: AccessControlRequestTemplate[],
  productUseCaseTemplateId: string | undefined,
): TemplateGroup[] {
  if (templates.length === 0 || outputPorts.length === 0) return [];

  const groups: TemplateGroup[] = [];
  const technologyToGroupIndex = new Map<string | undefined, number>();

  for (const port of outputPorts) {
    const template = resolveTemplateForPort(
      port,
      templates,
      productUseCaseTemplateId,
    );
    if (!template) continue;

    const technologyName = port.descriptor?.technology as string | undefined;
    const existingIndex = technologyToGroupIndex.get(technologyName);
    if (existingIndex !== undefined) {
      groups[existingIndex].outputPorts.push(port);
    } else {
      technologyToGroupIndex.set(technologyName, groups.length);
      groups.push({ template, technologyName, outputPorts: [port] });
    }
  }

  return groups;
}
